import React, { useState, useEffect } from 'react';
import { Flame, ArrowRight, BadgeCheck, GraduationCap, Megaphone, Truck, Settings, MonitorSmartphone, ChefHat, LifeBuoy, Check, ShieldCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FRANCHISE_FORMATS, FRANCHISE_SUPPORT, PROCESS_STEPS, IMG, BRAND } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import { createEnquiry, paymentsConfig, createFranchisePayment, verifyFranchisePayment } from '../lib/api';
import Seo from '../components/Seo';

const iconMap = { BadgeCheck, GraduationCap, Megaphone, Truck, Settings, MonitorSmartphone, ChefHat, LifeBuoy };

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Franchise() {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState({ enabled: false, fee_inr: 0 });
  const [payNow, setPayNow] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', phone:'', country:'', city:'', territory:'', format:'', budget:'', experience:'', space:'', source:'', message:'', consent:false });

  useEffect(() => { paymentsConfig().then(setPay).catch(() => {}); }, []);

  const onChange = (k, v) => setForm(f => ({...f, [k]: v}));

  const startPayment = async (enquiryId) => {
    const ok = await loadRazorpayScript();
    if (!ok) { toast({ title: 'Could not load payment', description: 'Please try again.', variant: 'destructive' }); return; }
    const order = await createFranchisePayment(enquiryId);
    return new Promise((resolve) => {
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: 'Zayka Sphere',
        description: 'Franchise Booking Fee',
        image: BRAND.logo,
        prefill: { name: form.name, email: form.email, contact: form.phone },
        theme: { color: '#f97316' },
        handler: async (res) => {
          try {
            await verifyFranchisePayment({
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast({ title: 'Payment successful 🎉', description: 'Your franchise booking is confirmed. We\'ll be in touch shortly.' });
          } catch {
            toast({ title: 'Payment received', description: 'We could not auto-verify — our team will confirm manually.', variant: 'destructive' });
          }
          resolve(true);
        },
        modal: { ondismiss: () => { toast({ title: 'Enquiry submitted', description: 'Payment cancelled — you can pay the booking fee later.' }); resolve(false); } },
      });
      rzp.open();
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast({ title: 'Consent required', description: 'Please agree to be contacted.', variant: 'destructive' }); return; }
    if (!form.name || !form.email) { toast({ title: 'Missing details', description: 'Please add your name and email.' }); return; }
    setBusy(true);
    try {
      const enquiry = await createEnquiry({ ...form, type: 'franchise' });
      if (pay.enabled && payNow && enquiry?.id) {
        await startPayment(enquiry.id);
      } else {
        toast({ title: 'Enquiry submitted', description: 'Thanks! Our franchise team will reach out shortly.' });
      }
      setForm({ name:'', email:'', phone:'', country:'', city:'', territory:'', format:'', budget:'', experience:'', space:'', source:'', message:'', consent:false });
      setPayNow(false);
    } catch (err) {
      toast({ title: 'Submission failed', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-24">
      <Seo title="Franchise Opportunity — Own a Zayka Sphere" path="/franchise" description="Become a Zayka Sphere franchise partner. Low-investment Express, Dine-In & Lounge formats with full training, marketing & supply support. Apply online today." />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.interior} alt="franchise" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-black" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-orange-500/40 bg-orange-500/10 text-orange-300 text-xs uppercase tracking-[0.25em] mb-6">
            <Flame className="w-3.5 h-3.5" /> Franchise Opportunity
          </div>
          <h1 className="font-display text-5xl md:text-7xl max-w-4xl leading-[0.95]">Bring Zayka Sphere <span className="text-orange-500 fire-glow">To Your City</span></h1>
          <p className="mt-6 max-w-2xl text-white/70 text-lg">Own a food business powered by a bold brand, scalable systems and a global expansion vision. We are looking for ambitious franchise partners to build the next generation of food businesses with us.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#enquiry"><Button className="h-12 px-6 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">Apply For Franchise</Button></a>
            <a href={BRAND.whatsapp} target="_blank" rel="noreferrer"><Button variant="outline" className="h-12 px-6 rounded-full border-orange-500/60 text-orange-400 hover:bg-orange-500 hover:text-white font-bold uppercase tracking-widest bg-transparent">Talk To Our Team</Button></a>
          </div>
        </div>
      </section>

      {/* FORMATS */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Formats</div>
            <h2 className="font-display text-4xl md:text-5xl">Franchise <span className="text-orange-500">Opportunities</span></h2>
            <p className="mt-3 text-white/60">Three formats built for different markets, footprints and investment levels.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {FRANCHISE_FORMATS.map((f, i) => (
              <Card key={i} className="food-card bg-neutral-900 border border-white/10 rounded-2xl p-7 relative overflow-hidden">
                <div className="absolute top-4 right-5 font-display text-6xl text-orange-500/20">{f.no}</div>
                <h3 className="font-bebas text-3xl tracking-wide">{f.title}</h3>
                <p className="mt-2 text-white/60 text-sm">{f.desc}</p>
                <div className="mt-6 space-y-3 text-sm">
                  <div><div className="text-white/50 uppercase text-xs tracking-widest">Space</div><div className="font-semibold">{f.space}</div></div>
                  <div><div className="text-white/50 uppercase text-xs tracking-widest">Estimated Investment</div><div className="font-semibold text-orange-400">{f.invest}</div></div>
                  <div><div className="text-white/50 uppercase text-xs tracking-widest">Franchise Fee</div><div className="font-semibold">{f.fee}</div></div>
                </div>
                <a href="#enquiry" className="mt-6 inline-flex items-center gap-1 text-orange-500 hover:text-orange-400 font-semibold uppercase tracking-widest text-sm">Enquire Now <ArrowRight className="w-4 h-4" /></a>
              </Card>
            ))}
          </div>
          <p className="text-xs text-white/40 mt-6 text-center max-w-3xl mx-auto">All investment figures are indicative and may vary depending on location, format, size, construction, equipment, licensing and market conditions. Final commercial terms are subject to the franchise agreement.</p>
        </div>
      </section>

      {/* SUPPORT */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Support</div>
            <h2 className="font-display text-4xl md:text-5xl">Why Partner With <span className="text-orange-500">Zayka Sphere</span></h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FRANCHISE_SUPPORT.map((s, i) => {
              const Icon = iconMap[s.icon];
              return (
                <Card key={i} className="food-card bg-neutral-900 border border-white/10 rounded-2xl p-6">
                  <div className="w-12 h-12 rounded-xl bg-orange-500/10 border border-orange-500/40 flex items-center justify-center text-orange-500"><Icon className="w-6 h-6" /></div>
                  <h3 className="mt-4 font-bebas text-xl tracking-wide">{s.title}</h3>
                  <p className="mt-1 text-sm text-white/60">{s.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Process</div>
            <h2 className="font-display text-4xl md:text-5xl">How To Become <span className="text-orange-500">A Partner</span></h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {PROCESS_STEPS.map((p, i) => (
              <div key={i} className="relative rounded-2xl border border-white/10 bg-neutral-900 p-6">
                <div className="font-display text-4xl text-orange-500">{p.no}</div>
                <h4 className="mt-3 font-bebas text-xl">{p.title}</h4>
                <p className="mt-2 text-white/60 text-sm">{p.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/global-expansion" className="text-orange-500 hover:text-orange-400 font-semibold uppercase tracking-widest text-sm inline-flex items-center gap-1">Exploring an international market? See global opportunities <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* ENQUIRY FORM */}
      <section id="enquiry" className="py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Franchise Enquiry</div>
            <h2 className="font-display text-4xl md:text-5xl">Ready To Build Zayka Sphere <span className="text-orange-500">In Your Market?</span></h2>
            <p className="mt-3 text-white/60">Share a few details and our franchise team will get in touch.</p>
          </div>
          <form onSubmit={onSubmit} className="bg-neutral-900 border border-white/10 rounded-3xl p-6 md:p-10 grid md:grid-cols-2 gap-5">
            <Input placeholder="Full Name" value={form.name} onChange={e=>onChange('name', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Email" type="email" value={form.email} onChange={e=>onChange('email', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Phone / WhatsApp" value={form.phone} onChange={e=>onChange('phone', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Country" value={form.country} onChange={e=>onChange('country', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="City" value={form.city} onChange={e=>onChange('city', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Preferred Territory" value={form.territory} onChange={e=>onChange('territory', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Select value={form.format} onValueChange={v=>onChange('format', v)}>
              <SelectTrigger className="h-12 bg-black/50 border-white/10"><SelectValue placeholder="Preferred Franchise Format" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="express">Express / Cafe Format</SelectItem>
                <SelectItem value="standard">Standard Dine-In Restaurant</SelectItem>
                <SelectItem value="premium">Premium Lounge Restaurant</SelectItem>
                <SelectItem value="master">Master Franchise</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Estimated Investment Budget" value={form.budget} onChange={e=>onChange('budget', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Business Experience" value={form.experience} onChange={e=>onChange('experience', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="Available Space (Sq. Ft.)" value={form.space} onChange={e=>onChange('space', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Input placeholder="How did you hear about us?" value={form.source} onChange={e=>onChange('source', e.target.value)} className="h-12 bg-black/50 border-white/10" />
            <Textarea placeholder="Message" value={form.message} onChange={e=>onChange('message', e.target.value)} className="md:col-span-2 min-h-[110px] bg-black/50 border-white/10" />
            <label className="md:col-span-2 flex items-start gap-3 text-sm text-white/70">
              <Checkbox checked={form.consent} onCheckedChange={v=>onChange('consent', !!v)} className="mt-0.5 border-orange-500 data-[state=checked]:bg-orange-500" />
              <span>I agree to be contacted regarding Zayka Sphere franchise opportunities.</span>
            </label>
            {pay.enabled && (
              <label className="md:col-span-2 flex items-start gap-3 rounded-2xl border border-orange-500/30 bg-orange-500/5 p-4" data-testid="pay-fee-option">
                <Checkbox checked={payNow} onCheckedChange={v=>setPayNow(!!v)} className="mt-0.5 border-orange-500 data-[state=checked]:bg-orange-500" />
                <span className="text-sm text-white/80">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-orange-400"><ShieldCheck className="w-4 h-4" /> Fast-track my application</span>
                  <span className="block mt-1 text-white/60">Pay a refundable booking fee of <span className="text-white font-bold">₹{pay.fee_inr}</span> securely via Razorpay to reserve your territory review slot. You'll pay right after submitting.</span>
                </span>
              </label>
            )}
            <Button type="submit" disabled={busy} data-testid="franchise-submit" className="md:col-span-2 h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest"><Check className="w-4 h-4 mr-2" /> {busy ? 'Please wait…' : (pay.enabled && payNow ? `Submit & Pay ₹${pay.fee_inr}` : 'Submit Franchise Enquiry')}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
